/**
 * @doo-events/design-tokens v2 — tema de EVENTO em runtime (semantic/event.css)
 *
 *   import { applyEventAccent } from '@doo-events/design-tokens/event-theme';
 *   applyEventAccent(document.documentElement, evento.theme.primaryColor);
 *
 * Escreve só --event-accent, --event-accent-2 e --event-on-accent e liga
 * `data-event-accent`; o CSS do pacote faz o resto. Nunca escreve token
 * canônico (--primary etc.): isso é NFR6 e o guard reprova.
 *
 * A cor do organizador só é aceita se o melhor texto sobre ela (branco ou a
 * tinta da marca) tiver contraste WCAG ≥ 4,5:1. Abaixo disso o evento fica
 * com a cor da marca — botão ilegível não é preferência de tema.
 */

/** Texto claro e escuro candidatos (= --btn-text e --brand-ink da LP). */
export const ON_LIGHT = '#1C0A51';
export const ON_DARK = '#FFFFFF';
export const MIN_CONTRAST = 4.5;

/** `#rgb`/`#rrggbb` → [r,g,b] 0–255, ou null se não for hex válido. */
export function parseHex(color) {
  if (typeof color !== 'string') return null;
  const m = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
}

/** Luminância relativa WCAG 2.x. */
export function luminance([r, g, b]) {
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Razão de contraste WCAG entre duas cores hex. */
export function contrast(a, b) {
  const la = luminance(parseHex(a));
  const lb = luminance(parseHex(b));
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Resolve as variáveis do tema de evento, ou null se a cor não serve.
 * @returns {{ '--event-accent': string, '--event-accent-2': string, '--event-on-accent': string, contrast: number } | null}
 */
export function eventAccentVars(accent, accent2) {
  if (!parseHex(accent)) return null;
  const onDark = contrast(accent, ON_DARK);
  const onLight = contrast(accent, ON_LIGHT);
  const on = onDark >= onLight ? ON_DARK : ON_LIGHT;
  const best = Math.max(onDark, onLight);
  if (best < MIN_CONTRAST) return null;
  return {
    '--event-accent': accent,
    '--event-accent-2': parseHex(accent2) ? accent2 : accent,
    '--event-on-accent': on,
    contrast: Math.round(best * 100) / 100,
  };
}

const VARS = ['--event-accent', '--event-accent-2', '--event-on-accent'];

/**
 * Aplica (ou limpa) o tema de evento num elemento — normalmente o <html>, para
 * os portais herdarem. Devolve true se a cor do evento foi aceita.
 */
export function applyEventAccent(el, accent, accent2) {
  const vars = eventAccentVars(accent, accent2);
  if (!vars) {
    clearEventAccent(el);
    return false;
  }
  for (const name of VARS) el.style.setProperty(name, vars[name]);
  el.setAttribute('data-event-accent', '');
  return true;
}

export function clearEventAccent(el) {
  for (const name of VARS) el.style.removeProperty(name);
  el.removeAttribute('data-event-accent');
}
